import classNames from 'classnames';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button, { ButtonWithLoadingState } from '../components/Button';
import { MenuItem, MenuSeparator } from '../components/Menu';
import { ModalTitle } from '../components/Modal';
import { BaseModalContainer } from '../components/ModalContainer';
import OverflowMenu from '../components/OverflowMenu';
import FormField from '../components/formInternals/FormField';
import { TopicDetached } from '../graphql/generated/graphql';
import copyIcon from '../icons/non-prefilled/16x16/copy.svg';
import overflowMenuIcon from '../icons/non-prefilled/16x16/overflowMenu.svg';
import trashIcon from '../icons/non-prefilled/16x16/trash.svg';
import { useAllSlimPostMap } from '../post/useAllSlimPosts';
import type { EntitySelectConfig } from '../search/entitySearch/CategorizedEntitySelect';
import CategorizedEntitySelect from '../search/entitySearch/CategorizedEntitySelect';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import usePreventLeaving from '../utils/usePreventLeaving';
import useShortcut from '../utils/useShortcut';
import DiscardSectionChangesModalContent from './DiscardSectionChangesModalContent';
import styles from './EditSidebarSectionsModal.module.scss';
import SectionItemList from './SectionItemList';
import SectionList from './SectionList';
import type { Section } from './SidebarSectionsProvider';
import {
  PremadeSections,
  useSidebarSectionsContext,
} from './SidebarSectionsProvider';
import useExternalPostPlatforms from './useExternalPostPlatforms';

interface ScrollablePaneProps {
  children: React.ReactNode;
  action: React.ReactNode;
}
const ScrollablePane = ({ children, action }: ScrollablePaneProps) => {
  const [isAtBottom, setIsAtBottom] = useState(false);

  return (
    <div
      className={classNames(styles.scrollablePane, {
        [styles.atBottom]: isAtBottom,
      })}
    >
      <div className={styles.innerContainer}>
        {children}
        <Waypoint
          onEnter={() => {
            setIsAtBottom(true);
          }}
          onLeave={() => {
            setIsAtBottom(false);
          }}
        />
      </div>
      <div className={styles.actionContainer}>{action}</div>
    </div>
  );
};

interface SectionItemsSelectProps {
  section: Section;
  onAddItem: (item: Section['items'][number]) => void;
}

const SectionItemsSelect = ({
  section,
  onAddItem,
}: SectionItemsSelectProps) => {
  const config = useMemo<EntitySelectConfig>(() => {
    const itemIds = section.items.map((item) => item.id);
    const filterItems = ({ id }: { id: string }) => !itemIds.includes(id);
    return {
      template: {
        filter: filterItems,
      },
      post: {
        filter: filterItems,
      },
      topic: {
        filter: filterItems,
      },
      order: ['topic', 'template', 'post'] as const,
    };
  }, [section]);

  return (
    <CategorizedEntitySelect
      isDisabled={section.premade}
      controlShouldRenderValue={false}
      onChange={(value) => {
        if (!value) return;
        const item =
          value.type === 'topic'
            ? ({
                __typename: 'Topic',
                id: value.id,
                detached: TopicDetached.None,
              } as const)
            : ({
                __typename: 'Post',
                id: value.id,
              } as const);

        onAddItem(item);
      }}
      placeholder={
        section.premade
          ? 'Section items are automatically added'
          : 'Add posts and topics to this section'
      }
      config={config}
      value={null}
      menuPlacement="top"
    />
  );
};

interface EmptyMessageProps {
  section: Section;
}
const EmptyMessage = ({ section }: EmptyMessageProps) => {
  const emptyMessage = (() => {
    if (!section.premade) {
      return 'No selected posts or topics yet. Search to add new.';
    }

    if (section.id === PremadeSections.INTEGRATIONS) {
      return 'No integrations to show.';
    }

    return 'No topics to show.';
  })();

  return <span className={styles.emptyStateMessage}>{emptyMessage}</span>;
};

const useSectionItems = (section: Section) => {
  const allSlimPostsMap = useAllSlimPostMap();
  const allTopicsMap = useAllTopicMap();
  const platforms = useExternalPostPlatforms();
  const { myTopics, allTopics } = useSidebarSectionsContext();

  if (section.id === PremadeSections.ALL_TOPICS) {
    return allTopics;
  }

  if (section.id === PremadeSections.MY_TOPICS && myTopics) {
    return myTopics;
  }

  if (section.id === PremadeSections.INTEGRATIONS) {
    return platforms;
  }

  return _.compact(
    section.items.map((item) => {
      if (item.__typename === 'Post') {
        return allSlimPostsMap[item.id];
      }
      if (item.__typename === 'Topic') {
        return allTopicsMap[item.id];
      }
      return null;
    }),
  );
};

interface SectionPaneProps {
  section: Section;
  onDeleteSection: () => void;
  onDuplicateSection: () => void;
}

const SectionPane = ({
  section,
  onDeleteSection,
  onDuplicateSection,
}: SectionPaneProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const {
    updateSection,
    moveItemInSection,
    addItemToSection,
    removeItemFromSection,
  } = useSidebarSectionsContext();
  const items = useSectionItems(section);

  return (
    <ScrollablePane
      action={
        <SectionItemsSelect
          section={section}
          onAddItem={(item) => addItemToSection(section.id, item)}
        />
      }
    >
      <>
        <div className={styles.controls}>
          <FormField
            className={styles.nameField}
            disabled={section.premade}
            type="text"
            value={section.name}
            margin="none"
            placeholder="Section"
            onChange={(event) => {
              updateSection({
                id: section.id,
                name: event.target.value,
              });
            }}
          />
          {!section.premade && (
            <OverflowMenu
              button={
                <Button
                  className={styles.overflowMenuButton}
                  icon={overflowMenuIcon}
                  aria-label="More options"
                />
              }
              open={isOverflowMenuOpen}
              onOpenChange={setIsOverflowMenuOpen}
              placement="bottom-start"
            >
              <MenuItem wrap icon={copyIcon} onClick={onDuplicateSection}>
                Duplicate section
              </MenuItem>
              <MenuSeparator />
              <MenuItem wrap icon={trashIcon} danger onClick={onDeleteSection}>
                Delete section
              </MenuItem>
            </OverflowMenu>
          )}
        </div>

        {items.length === 0 && <EmptyMessage section={section} />}

        <SectionItemList
          disabled={section.premade && section.id !== PremadeSections.MY_TOPICS}
          onMove={(from, to) => moveItemInSection(section.id, from, to)}
        >
          {items.map((item, index) => (
            <SectionItemList.Row
              key={item.id}
              item={item}
              index={index}
              onDeleteClick={
                section.premade
                  ? undefined
                  : () => removeItemFromSection(section.id, item.id)
              }
            />
          ))}
        </SectionItemList>
      </>
    </ScrollablePane>
  );
};

export const SIDEBAR_SECTIONS_CLICK_EVENTS = new WeakSet<MouseEvent>();

interface InnerEditSidebarSectionsModalProps {
  requestClose: () => void;
  forceClose: () => void;
}

export const InnerEditSidebarSectionsModal = ({
  requestClose,
  forceClose,
}: InnerEditSidebarSectionsModalProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const {
    allSections,
    sidebarSections,
    toggleSectionVisibility,
    saveSections,
    addSection,
    deleteSection,
    moveSection,
  } = useSidebarSectionsContext();

  const dispatch = useAppDispatch();
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  const selectedSection = allSections.find(
    (section) => section.id === selectedSectionId,
  );

  return (
    <ClickOutsideHandler
      className={styles.container}
      onClickOutside={(event) => {
        if (
          !isSaving &&
          event instanceof MouseEvent &&
          !SIDEBAR_SECTIONS_CLICK_EVENTS.has(event)
        ) {
          requestClose();
        }
      }}
    >
      <div className={styles.header}>
        <ModalTitle
          small
          noMargin
          close={() => {
            if (!isSaving) requestClose();
          }}
        >
          Edit sidebar sections
        </ModalTitle>
      </div>
      <div className={styles.body}>
        <ScrollablePane
          action={
            <Button
              className={styles.createSectionButton}
              variant="secondary"
              onClick={() => {
                const section = addSection({ name: 'New section' });
                dispatch(UIActions.setSelectedSectionId(section.id));
              }}
            >
              Create new section
            </Button>
          }
        >
          <SectionList onMove={moveSection}>
            {allSections.map((section, index) => {
              return (
                <SectionList.Row
                  section={section}
                  visible={sidebarSections.some(({ id }) => id === section.id)}
                  selected={selectedSectionId === section.id}
                  index={index}
                  key={section.id}
                  onVisibilityButtonClick={() =>
                    toggleSectionVisibility(section.id)
                  }
                  onClick={() =>
                    dispatch(UIActions.setSelectedSectionId(section.id))
                  }
                />
              );
            })}
          </SectionList>
        </ScrollablePane>
        {selectedSection && (
          <SectionPane
            section={selectedSection}
            onDeleteSection={() => {
              deleteSection(selectedSection.id);
              dispatch(
                UIActions.setSelectedSectionId(allSections.at(0)?.id ?? null),
              );
            }}
            onDuplicateSection={() => {
              const duplicatedSection = addSection(selectedSection);
              dispatch(UIActions.setSelectedSectionId(duplicatedSection.id));
            }}
          />
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.text}>Changes are not live until you save</div>
        <div className={styles.buttons}>
          <Button
            variant="secondary"
            disabled={isSaving}
            onClick={requestClose}
          >
            Discard
          </Button>
          <ButtonWithLoadingState
            loading={isSaving}
            variant="primary"
            className={styles.saveButton}
            onClick={async () => {
              setIsSaving(true);
              try {
                await saveSections();
                forceClose();
              } finally {
                setIsSaving(false);
              }
            }}
          >
            Finalize and save
          </ButtonWithLoadingState>
        </div>
      </div>
    </ClickOutsideHandler>
  );
};

const EditSidebarSectionsModal = () => {
  const { unsavedSections } = useSidebarSectionsContext();

  const [
    isDiscardSectionChangesModalOpen,
    setIsDiscardSectionChangesModalOpen,
  ] = useState(false);

  const dispatch = useAppDispatch();
  const selectedSectionId = useAppSelector(
    (state) => state.ui.selectedSectionId,
  );

  if (selectedSectionId == null && isDiscardSectionChangesModalOpen) {
    setIsDiscardSectionChangesModalOpen(false);
  }

  usePreventLeaving({
    skip: selectedSectionId == null || unsavedSections.length === 0,
  });

  const requestClose = () => {
    if (unsavedSections.length > 0) {
      setIsDiscardSectionChangesModalOpen(true);
    } else {
      dispatch(UIActions.setSelectedSectionId(null));
    }
  };

  useShortcut(
    'esc',
    (e) => {
      e.preventDefault();
      requestClose();
    },
    { global: true },
  );

  return (
    <BaseModalContainer sidebarAware open={selectedSectionId != null}>
      {isDiscardSectionChangesModalOpen ? (
        <DiscardSectionChangesModalContent
          unsavedSections={unsavedSections}
          onBack={() => setIsDiscardSectionChangesModalOpen(false)}
          onConfirm={() => dispatch(UIActions.setSelectedSectionId(null))}
        />
      ) : (
        <InnerEditSidebarSectionsModal
          requestClose={requestClose}
          forceClose={() => dispatch(UIActions.setSelectedSectionId(null))}
        />
      )}
    </BaseModalContainer>
  );
};

export default EditSidebarSectionsModal;
